import * as Yup from 'yup';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Autocomplete,
  Button,
  Divider,
  Link,
  Table,
  TableBody,
  TableContainer,
  TextField,
} from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { TABLE_HEAD_CELL_TYPE } from 'src/utils/common-types';

import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { getPaginatedEmployees } from 'src/redux/slices/employees';
import { getPaginatedUsers, IListUser, UserStatus } from 'src/redux/slices/users';
import {
  createStationeryDispatch,
  TCreateStationeryDispatch,
  updateStationeryDispatch,
} from 'src/redux/slices/stationery-dispatch';

import Scrollbar from 'src/components/scrollbar';
import { TableHeadCustom } from 'src/components/table';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { useSnackbar as useAppSnackbar } from 'src/components/snackbar';
import CustomDatePicker from 'src/components/custom-datepicker/custom-datepicker';

import { EmployeeStatus } from 'src/types/employee';
import { IStationeryItemListItem } from 'src/types/stationery-item';
import {
  IStationeryDispatchDetailedItem,
  IStationeryInsufficientInventoryItem,
} from 'src/types/stationery-dispatch';

import { paths } from '../../routes/paths';
import StationeryDispatchNewItemRow from './stationery-dispatch-new-item-row';

const LINE_ITEM_HEADERS: TABLE_HEAD_CELL_TYPE[] = [
  { id: 'itemNo', label: '#', align: 'left', primary: true },
  { id: 'stationeryItem', label: 'Stationery Item', align: 'left', primary: true },
  { id: 'quantity', label: 'Quantity', align: 'left', primary: true },
  { id: 'receivedBy', label: 'Received By', align: 'left', primary: true },
  { id: '', primary: false },
];

type Props = {
  currentDispatch?: IStationeryDispatchDetailedItem;
};

export default function StationeryDispatchNewEditForm({ currentDispatch }: Props) {
  const dispatch = useAppDispatcher();
  const { enqueueSnackbar } = useAppSnackbar();
  const navigate = useNavigate();

  const { users } = useAppSelector((state) => state.users);

  const [userSearchQuery, setUserSearchQuery] = useState('');

  // Populated from a failed submit's insufficient-inventory response (see
  // onSubmit's catch block) so the offending line(s) can be highlighted —
  // every line sharing a flagged stationeryItem, since the shortfall is
  // the *combined* quantity across duplicate lines, not any single one.
  const [insufficientItems, setInsufficientItems] = useState<
    IStationeryInsufficientInventoryItem[]
  >([]);
  const insufficientItemsByStationeryItem = useMemo(
    () => new Map(insufficientItems.map((item) => [item.stationeryItem, item])),
    [insufficientItems]
  );

  const NewStationeryDispatchSchema = Yup.object().shape({
    issuedBy: Yup.string().required('Issued by is required'),
    dispatchDate: Yup.date().typeError('Invalid date').required('Dispatch date is required'),
    remarks: Yup.string().nullable(),
    items: Yup.array()
      .of(
        Yup.object().shape({
          stationeryItem: Yup.string().required('Stationery item is required'),
          quantity: Yup.number()
            .typeError('')
            .moreThan(0, 'Quantity must be greater than zero')
            .required('Quantity is required'),
          receivedBy: Yup.string().required('Received by is required'),
        })
      )
      .min(1, 'At least one item is required')
      .required('Items are required'),
  });

  const defaultItem = useMemo(
    () => ({
      stationeryItem: '',
      quantity: 1,
      receivedBy: '',
    }),
    []
  );

  const defaultValues = useMemo(
    () => ({
      issuedBy: currentDispatch?.issuedBy._id ?? '',
      dispatchDate: currentDispatch?.dispatchDate ? new Date(currentDispatch.dispatchDate) : new Date(),
      remarks: currentDispatch?.remarks ?? null,
      items: currentDispatch?.items.map((item) => ({
        _id: item._id,
        stationeryItem: item.stationeryItem._id,
        quantity: item.quantity,
        receivedBy: item.receivedBy?._id ?? '',
      })) ?? [defaultItem],
    }),
    [currentDispatch, defaultItem]
  );

  const methods = useForm({
    resolver: yupResolver(NewStationeryDispatchSchema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    reset,
    control,
    handleSubmit,
    getValues,
    formState: { isSubmitting, errors },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  // Consecutive lines are very often the same recipient, so a newly added
  // line (not the first) starts pre-filled with whatever the previous
  // line's receivedBy was — free to change, just saves a re-pick.
  const handleAddItem = () => {
    const items = getValues('items');
    const lastReceivedBy = items[items.length - 1]?.receivedBy ?? '';
    append({ ...defaultItem, receivedBy: lastReceivedBy });
  };

  const onSubmit = handleSubmit(async (data) => {
    // Cleared on every attempt — a resubmit that fixes the shortfall (or
    // fails for an unrelated reason) shouldn't keep stale rows highlighted.
    setInsufficientItems([]);

    try {
      const payload: TCreateStationeryDispatch = {
        ...data,
        remarks: data.remarks ?? null,
      };

      if (currentDispatch) {
        await dispatch(
          updateStationeryDispatch({
            dispatchId: currentDispatch._id,
            dispatchTransaction: payload,
          })
        ).unwrap();

        enqueueSnackbar('Dispatch updated');
      } else {
        await dispatch(createStationeryDispatch({ dispatchTransaction: payload })).unwrap();

        enqueueSnackbar('Dispatch created');
      }

      navigate(paths.dashboard.stationeryDispatch.root);
      reset();
    } catch (e) {
      if (typeof e === 'string') {
        enqueueSnackbar(e, {
          variant: 'error',
        });
      } else if (Array.isArray(e?.data) && e.data.length > 0) {
        const failedItems = e.data as IStationeryInsufficientInventoryItem[];
        setInsufficientItems(failedItems);

        const detail = failedItems
          .map(
            (item) =>
              `${item.itemName} (need ${item.requestedQuantity}, have ${item.availableQuantity})`
          )
          .join('; ');

        enqueueSnackbar(`Insufficient stock: ${detail}`, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(e?.message, {
          variant: 'error',
        });
      }
    }
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchPaginatedUsers = useCallback(
    debounce((query) => {
      dispatch(
        getPaginatedUsers({
          page: 0,
          limit: 25,
          firstName: query,
          email: null,
          lastName: null,
          permissions: [],
          sort: 'asc',
          sortBy: 'firstName',
          status: [UserStatus.ACTIVE],
        })
      );
    }, 500),
    [dispatch]
  );

  useEffect(() => {
    fetchPaginatedUsers(userSearchQuery);

    return fetchPaginatedUsers.cancel;
  }, [userSearchQuery, fetchPaginatedUsers]);

  // Fetched once, unfiltered, and filtered/searched client-side per row
  // instead of a shared debounced server search, since receivedBy now
  // lives on every row instead of the header, and each row needs to
  // search independently without stepping on the others.
  useEffect(() => {
    dispatch(
      getPaginatedEmployees({
        page: null,
        limit: null,
        generalSearch: null,
        sortBy: 'firstName',
        sort: 'asc',
        status: EmployeeStatus.ACTIVE,
      })
    );
  }, [dispatch]);

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid container spacing={3}>
        <Grid xs={12}>
          <Card sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid
                mb={1}
                display="flex"
                mt={2}
                alignItems="center"
                justifyContent="space-between"
                width={1}
              >
                <Typography variant="subtitle2">Dispatch Information</Typography>
              </Grid>

              <Grid xs={12} md={6}>
                <Controller
                  name="dispatchDate"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <CustomDatePicker
                      disableFuture
                      label="Dispatch Date"
                      value={field.value}
                      onChange={(newValue) => field.onChange(newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!error,
                          helperText: error?.message,
                          required: true,
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid xs={12} md={6} />

              <Grid xs={12} md={6}>
                <Controller
                  name="issuedBy"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Autocomplete
                      {...field}
                      fullWidth
                      multiple={false}
                      options={users.users}
                      isOptionEqualToValue={(
                        option: IListUser | string,
                        value: IListUser | string
                      ) => {
                        const optionId = typeof option === 'string' ? option : option?._id;
                        const valueId = typeof value === 'string' ? value : value?._id;
                        return optionId === valueId;
                      }}
                      getOptionLabel={(option: IListUser | string) => {
                        if (!option) return '';
                        if (typeof option === 'string') {
                          if (option.length === 0) return option;
                          const value = users.users.find(
                            (user: IListUser) => user._id === option
                          );
                          return value
                            ? `${value.firstName}${value.lastName ? ` ${value.lastName}` : ''}`
                            : '';
                        }
                        return `${option.firstName}${option.lastName ? ` ${option.lastName}` : ''} (${option.email})`;
                      }}
                      renderOption={(props, option: IListUser | string) => {
                        if (typeof option === 'string') {
                          return (
                            <li {...props} key={option}>
                              {option}
                            </li>
                          );
                        }
                        return (
                          <li {...props} key={option._id}>
                            {option.firstName}
                            {option.lastName ? ` ${option.lastName}` : ''} ({option.email})
                          </li>
                        );
                      }}
                      onInputChange={(_, value, reason) => {
                        if (reason === 'input') setUserSearchQuery(value);
                        if (reason === 'clear') setUserSearchQuery('');
                      }}
                      renderInput={(params) => (
                        <TextField
                          label="Issued By (Staff)"
                          required
                          error={!!error}
                          helperText={error?.message}
                          {...params}
                        />
                      )}
                      onChange={(event, newValue) => {
                        field.onChange(typeof newValue === 'string' ? newValue : newValue?._id ?? '');
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid xs={12}>
                <RHFTextField name="remarks" label="Remarks" multiline rows={2} />
              </Grid>

              <Divider
                sx={{
                  width: 1,
                  my: 2,
                  borderStyle: 'dashed',
                }}
              />

              <Grid mb={1} xs={12} width="full">
                <Typography variant="subtitle2">Line Items</Typography>

                {typeof errors.items?.message === 'string' && (
                  <Typography variant="caption" color="error.main" component="p" mt={0.5}>
                    {errors.items.message}
                  </Typography>
                )}
              </Grid>

              <Grid xs={12}>
                <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '300px' }}>
                  <Scrollbar>
                    <Table stickyHeader size="small" sx={{ minWidth: 640 }}>
                      <TableHeadCustom headLabel={LINE_ITEM_HEADERS} />

                      <TableBody>
                        {fields.map((item, index) => (
                          <StationeryDispatchNewItemRow
                            key={item.id}
                            index={index}
                            remove={remove}
                            isLast={fields.length === 1 && index === 0}
                            initialItem={
                              currentDispatch?.items[index]?.stationeryItem as
                                | IStationeryItemListItem
                                | undefined
                            }
                            insufficientItemsByStationeryItem={insufficientItemsByStationeryItem}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </Scrollbar>
                </TableContainer>

                <Stack mt={2}>
                  <Button variant="soft" color="primary" onClick={handleAddItem}>
                    Add
                  </Button>
                </Stack>
              </Grid>
            </Grid>

            <Stack direction="row" justifyContent="space-between" gap={2} mt={4}>
              <Link component={RouterLink} href={paths.dashboard.stationeryDispatch.root}>
                <Button variant="outlined" color="inherit">
                  Cancel
                </Button>
              </Link>

              <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                {!currentDispatch ? 'Create Dispatch' : 'Save Changes'}
              </LoadingButton>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
